import React, { useCallback, useRef, useEffect, useMemo } from 'react';

import { includes, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
  Select,
  Input,
  Form,
  Autocomplete,
  RadioGroup,
  DateTime,
} from '~/components/Form';
import { t } from '~/helpers';

import * as S from './styles';

export default function MTable({
  sending,
  items,
  ignore,
  label,
  onChangeItem,
  fields,
  initials,
  onField,
  isSchema,
  onModify,
  isIgnore,
  onCallback,
}) {
  const formRef = useRef(null);

  const array = useMemo(
    () =>
      items.filter(
        (item) =>
          !includes(
            ignore.map((ig) => ig.id),
            item.id
          )
      ),
    [items, ignore]
  );

  const ignoreOptions = useCallback((value) => {
    if (!isEmpty(isIgnore)) {
      const options = value.filter(
        (v) =>
          !includes(
            ignore.map((ig) => ig[isIgnore]),
            v.value
          )
      );

      if (!isEmpty(initials)) {
        const isPush = fields
          .find((f) => f.id === isIgnore)
          .options.find((o) => o.value === initials[isIgnore]);

        options.push(isPush);
      }

      return options;
    }

    return value;
  }, []);

  const renderForm = useMemo(() => {
    if (isEmpty(fields)) {
      return <Select name="item" label={label} options={items} />;
    }

    return fields.map((field) => {
      switch (field.type) {
        case 'radio':
          return (
            <RadioGroup
              name={field.id}
              label={field.label}
              disabled={!!field.disabled}
              options={field.options}
              row
            />
          );
        case 'select':
          return (
            <Select
              name={field.id}
              label={field.label}
              disabled={!isEmpty(initials) && field.disabled}
              options={ignoreOptions(field.options)}
            />
          );
        case 'autocomplete':
          return (
            <Autocomplete
              name={field.id}
              label={field.option.label}
              url={field.option.url}
              unique={field.option.unique}
              title={field.label}
              disabled={!!field.disabled}
            />
          );
        case 'datetime':
          return (
            <DateTime
              name={field.id}
              label={field.label}
              disablePast={true}
              disabled={!isEmpty(initials) && field.disabled}
              shouldDisableDate={field.existentes}
              inputProps={{ readOnly: true }} // desabilita alteracao no campo input (digitacao)
            />
          );
        case 'text':
        case 'number':
        default:
          return (
            <Input
              name={field.id}
              label={field.label}
              type={field.type}
              disabled={!!field.disabled}
            />
          );
      }
    });
  }, [fields, label, array, ignore, isIgnore, initials, items]);

  const handleSubmit = useCallback(
    async (data, { reset }) => {
      try {
        const schema = Yup.object().shape(
          isEmpty(fields)
            ? {
              item: Yup.string().required(
                t('s.geral.validacao.campoobrigatorio')
              ),
            }
            : isSchema
        );

        await schema.validate(data, {
          abortEarly: false,
        });

        if (isEmpty(fields) || isEmpty(initials)) {
          const isData = isEmpty(fields)
            ? items.find((item) => item.id === data.item)
            : onField(data);

          onChangeItem(isData);
          onCallback({
            type: 'create',
            data: isData,
          });
        } else {
          onModify(onField(data));
          onCallback({
            type: 'modify',
            data: onField(data),
          });
        }
        reset();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        }
      }
    },
    [fields]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <S.Wrapper>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={initials}>
        {renderForm}
      </Form>
    </S.Wrapper>
  );
}

MTable.propTypes = {
  sending: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  label: PropTypes.string.isRequired,
  onChangeItem: PropTypes.func.isRequired,
  ignore: PropTypes.arrayOf(PropTypes.any).isRequired,
  fields: PropTypes.arrayOf(PropTypes.any).isRequired,
  initials: PropTypes.objectOf(PropTypes.any).isRequired,
  onField: PropTypes.func.isRequired,
  isSchema: PropTypes.objectOf(PropTypes.any).isRequired,
  onModify: PropTypes.func.isRequired,
  isIgnore: PropTypes.string.isRequired,
  onCallback: PropTypes.func.isRequired,
};
